import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';

import axios, { apiBaseUrl } from '../api/httpClient';
import IntegratedManagementOperationalRecords from './IntegratedManagementOperationalRecords';
import IntegratedManagementSourceReconciliation from './IntegratedManagementSourceReconciliation';
import IntegratedManagementWorkflows from './IntegratedManagementWorkflows';
import IntegratedManagementAutomation from './IntegratedManagementAutomation';
import './IntegratedManagementSystemPage.css';

interface Standard {
  code: string;
  name: string;
  programme: string;
  capabilities: string[];
}

interface ProgrammeAction {
  key: string;
  summary: string;
  status: string;
  statusCategory: string | null;
  assignee: string | null;
  dueDate: string | null;
  labels: string[];
  updatedAt: string | null;
}

interface Overview {
  generatedAt: string;
  standards: Standard[];
  sourceSnapshot: {
    snapshotAt: string | null;
    source: string | null;
    scope: string | null;
  };
  migration: {
    state?: string;
    sourceSystem?: string;
    targetSystem?: string;
    cutoverPermitted?: boolean;
    cutoverGate?: string;
    reconciliation?: { status?: string; normalizedRecordCount?: number };
    archive?: { name?: string; sha256?: string; fileCount?: number };
  };
  recordCounts: Record<string, number>;
  health: Record<string, number>;
  programmeActions: {
    epic: string;
    project?: string;
    scope?: string;
    total: number;
    statusCounts: Record<string, number>;
    items: ProgrammeAction[];
  };
  links: {
    sharePointEvidence: string;
    jiraEpic: string;
  };
}

interface RecordResponse {
  type: string;
  page: number;
  perPage: number;
  total: number;
  lastPage: number;
  items: Record<string, unknown>[];
  sourceSnapshotAt: string | null;
}

const recordTypes = [
  ['controls', 'Controls'],
  ['policyDocuments', 'Policy documents'],
  ['risks', 'Risks'],
  ['tasks', 'Tasks'],
  ['suppliers', 'Suppliers'],
  ['audits', 'Audits'],
  ['assertions', 'Control tests'],
  ['legislation', 'Compliance obligations'],
  ['aiSystems', 'AI systems'],
  ['people', 'People'],
] as const;

const metricLabels: Record<string, string> = {
  controlReviewsOverdue: 'Control reviews overdue',
  policyReviewsOverdue: 'Policy reviews overdue',
  livePolicyDocuments: 'Live policy documents',
  riskRegisterItems: 'Risk register items',
  openAdoptechTasks: 'Open migrated tasks',
  overdueSupplierReviews: 'Supplier reviews overdue',
  openJiraActions: 'Open Jira actions',
  overdueJiraActions: 'Jira actions overdue',
};

const formatTimestamp = (value: string | null | undefined): string => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('en-GB');
};

const textValue = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (value && typeof value === 'object') {
    const object = value as Record<string, unknown>;
    return textValue(object.full_name ?? object.displayName ?? object.name ?? object.email ?? '');
  }
  return '';
};

const recordName = (item: Record<string, unknown>): string => (
  textValue(item.name ?? item.summary ?? item.identifier ?? item.reference ?? item.id) || 'Unnamed record'
);

const recordOwner = (item: Record<string, unknown>): string => (
  textValue(item.owner ?? item.assignee ?? item.line_manager) || 'Unassigned'
);

const recordStatus = (item: Record<string, unknown>): string => {
  if (typeof item.status === 'string' && item.status) return item.status;
  if (typeof item.review_status === 'string' && item.review_status) return item.review_status;
  if (item.live === true) return 'live';
  if (item.archived === true) return 'archived';
  return 'not recorded';
};

const recordReview = (item: Record<string, unknown>): string => (
  textValue(item.next_review_date ?? item.review_date ?? item.updated_at ?? item.created_at) || 'Not recorded'
);

const IntegratedManagementSystemPage: React.FC = () => {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recordType, setRecordType] = useState('controls');
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState<RecordResponse | null>(null);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<Overview>(`${apiBaseUrl}/integrated-management-system`);
      setOverview(response.data);
    } catch {
      setError('The controlled SharePoint and Jira management-system sources could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    setError('');
    try {
      const response = await axios.get<RecordResponse>(
        `${apiBaseUrl}/integrated-management-system/records/${recordType}`,
        { params: { perPage: 100, ...(search.trim() ? { search: search.trim() } : {}) } }
      );
      setRecords(response.data);
    } catch {
      setError('The selected controlled records could not be loaded.');
    } finally {
      setRecordsLoading(false);
    }
  }, [recordType, search]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  return (
    <Container className="ims-page py-4">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <p className="ims-page__eyebrow">Gemstone governance</p>
          <h2>Integrated management system</h2>
          <p className="text-muted mb-0">One controlled view across quality, information security, environment and service management.</p>
        </div>
        <Button variant="outline-primary" onClick={loadOverview} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh sources'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && !overview ? (
        <div className="ims-page__loading"><Spinner animation="border" /><span>Loading SharePoint and Jira…</span></div>
      ) : overview && (
        <>
          {overview.migration.cutoverPermitted !== true && (
            <Alert variant="warning">
              <strong>Parallel run:</strong> Adoptech remains operational until reconciliation, backup/restore, access review and management approval are complete.
              {overview.migration.cutoverGate ? ` ${overview.migration.cutoverGate}` : ''}
            </Alert>
          )}

          <Row className="g-3 mb-4">
            {overview.standards.map((standard) => (
              <Col md={6} xl={3} key={standard.code}>
                <Card className="h-100 ims-page__standard">
                  <Card.Body>
                    <Badge bg={standard.programme.includes('surveillance') ? 'primary' : 'secondary'}>{standard.programme}</Badge>
                    <Card.Title className="mt-3">{standard.code}</Card.Title>
                    <Card.Subtitle className="text-muted mb-3">{standard.name}</Card.Subtitle>
                    <ul className="small ps-3 mb-0">
                      {standard.capabilities.map((capability) => <li key={capability}>{capability}</li>)}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <IntegratedManagementAutomation />
          <IntegratedManagementOperationalRecords standards={overview.standards.map((standard) => standard.code)} />
          <IntegratedManagementWorkflows />
          <IntegratedManagementSourceReconciliation />

          <Card className="mb-4">
            <Card.Body className="d-flex flex-wrap justify-content-between gap-3">
              <div>
                <strong>Controlled source snapshot</strong>
                <div className="small text-muted">{formatTimestamp(overview.sourceSnapshot.snapshotAt)} · {overview.sourceSnapshot.source}</div>
              </div>
              <div>
                <strong>{Object.values(overview.recordCounts).reduce((sum, value) => sum + value, 0)}</strong>
                <div className="small text-muted">normalized management-system records</div>
              </div>
              <div>
                <strong>{overview.programmeActions.total}</strong>
                <div className="small text-muted">Jira programme actions</div>
              </div>
              <a href={overview.links.sharePointEvidence} target="_blank" rel="noreferrer">Open controlled evidence in SharePoint</a>
            </Card.Body>
          </Card>

          <Row className="g-3 mb-4">
            {Object.entries(overview.health).map(([key, value]) => (
              <Col sm={6} lg={3} key={key}>
                <Card className="h-100 ims-page__metric">
                  <Card.Body><strong>{value}</strong><span>{metricLabels[key] ?? key}</span></Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="mb-4">
            <Card.Header><strong>Programme actions · {overview.programmeActions.scope ?? overview.programmeActions.epic}</strong></Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead><tr><th>Key</th><th>Action</th><th>Status</th><th>Owner</th><th>Due</th></tr></thead>
                <tbody>
                  {overview.programmeActions.items.map((action) => (
                    <tr key={action.key}>
                      <td><strong>{action.key}</strong></td>
                      <td>{action.summary}</td>
                      <td><Badge bg={action.statusCategory === 'done' ? 'success' : 'secondary'}>{action.status}</Badge></td>
                      <td>{action.assignee ?? 'Unassigned'}</td>
                      <td>{action.dueDate ?? 'Not set'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header><strong>Browse controlled records</strong></Card.Header>
            <Card.Body>
              <Form className="d-flex flex-wrap gap-2 align-items-end" onSubmit={(event) => { event.preventDefault(); loadRecords(); }}>
                <Form.Group>
                  <Form.Label>Record type</Form.Label>
                  <Form.Select aria-label="Record type" value={recordType} onChange={(event) => setRecordType(event.target.value)}>
                    {recordTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="flex-grow-1">
                  <Form.Label>Search</Form.Label>
                  <Form.Control aria-label="Search records" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, owner, status or standard" />
                </Form.Group>
                <Button type="submit" disabled={recordsLoading}>{recordsLoading ? 'Loading…' : 'Load records'}</Button>
              </Form>
            </Card.Body>
            {records && (
              <Card.Body className="p-0 border-top">
                <Table responsive hover className="mb-0 align-middle">
                  <thead><tr><th>Record</th><th>Status</th><th>Owner</th><th>Review / updated</th></tr></thead>
                  <tbody>
                    {records.items.map((item, index) => (
                      <tr key={`${recordName(item)}-${index}`}>
                        <td><strong>{recordName(item)}</strong></td>
                        <td>{recordStatus(item)}</td>
                        <td>{recordOwner(item)}</td>
                        <td>{recordReview(item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {records.items.length === 0 && <p className="text-muted p-4 mb-0">No controlled records match this filter.</p>}
              </Card.Body>
            )}
            {records && <Card.Footer className="small text-muted">Showing {records.items.length} of {records.total} records from {formatTimestamp(records.sourceSnapshotAt)}</Card.Footer>}
          </Card>
        </>
      )}
    </Container>
  );
};

export default IntegratedManagementSystemPage;
